import React, { useEffect, useState } from "react";
import { Switch, Route, Redirect } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { authProtectedRoutes } from "../../routes";
import { Loader } from "../../components/Loader";
import { Page500 } from "../../components/Utility";
import { getMe } from "../../store/actions";
import { APP_ROUTES } from "helpers/routeHelpers";
import { canSee, hasAnyAccess, homeRoute } from "helpers/access";
import { persistor } from "../../store";
import NotGranted from "./NotGranted";

const ACCESS_RECHECK_MS = 30 * 1000;

const DefaultLayout = ({ Layout }) => {
    let dispatch = useDispatch();
    const { tokenVerified, isLoading } = useSelector((state) => state.root);
    const { isAuthenticated } = useSelector((state) => state.userAuth);

    // Lowercase `type` — the API's GetMe response (spread verbatim into
    // userAuth.user by authReducer's ME_SUCCESS) uses the backend's field
    // name, not this app's own `Token`-style capitalization convention.
    // `.Type` here always read undefined, which made `Role` (and therefore
    // every permission check below) meaningless.
    const Role = useSelector((state) => state?.userAuth?.user?.type);

    useEffect(() => {
        if (isAuthenticated) {
            if (!tokenVerified) {
                dispatch(getMe()).catch(() => {});
            }
        }
    }, []);

    // Grants change while people are signed in: an admin switches Inbox or
    // MCAP access on (or off) in the console, then comes back to this tab.
    // Re-reading /me when the tab is shown again makes that take effect on the
    // spot — menu and routes follow the fresh flags — instead of only after a
    // reload. At most every 30 seconds, so tab-flipping costs nothing.
    useEffect(() => {
        if (!isAuthenticated || !tokenVerified) return undefined;
        let last = Date.now();
        const recheck = () => {
            if (document.hidden || Date.now() - last < ACCESS_RECHECK_MS) return;
            last = Date.now();
            dispatch(getMe()).catch(() => {});
        };
        document.addEventListener("visibilitychange", recheck);
        window.addEventListener("focus", recheck);
        return () => {
            document.removeEventListener("visibilitychange", recheck);
            window.removeEventListener("focus", recheck);
        };
    }, [isAuthenticated, tokenVerified, dispatch]);

    if (!isAuthenticated) {
        // return <Redirect to={APP_ROUTES.LOGIN} />;
        window.location.replace(APP_ROUTES.LOGIN);

    }

    // Case-insensitive on purpose: the backend's UserType enum is
    // ("USER" | "ADMIN"), all caps, while this app's own Roles constant
    // (constants/Roles.js, used for the permissions lists in routes/index.js)
    // spells the same role "Admin" for display purposes. Comparing them
    // case-sensitively — even after the `.Type`/`.type` fix above — would
    // never match, and would filter every route out for the only role that
    // exists here. Also no longer negated: keep the routes this role IS
    // permitted for, not the ones it isn't.
    const user = useSelector((state) => state?.userAuth?.user);

    // Role, then grant: each route also needs the grant its screen is behind
    // (mcapAccess for analytics, inboxAccess for the WhatsApp inbox — see
    // helpers/access.js), so a person with one grant never sees the other's
    // screens in the menu or by typing the URL.
    const roleBasedRoutes = authProtectedRoutes.filter(
        (item) =>
            item.permissions?.some((permission) => permission?.toUpperCase() === Role?.toUpperCase()) &&
            canSee(item, user),
    );

    // The server-side gates (McapAccessGuard on every MCAP analytics endpoint,
    // RequireInboxAccess on the inbox) are the ones that actually matter —
    // this is the client-side mirror, checked once here rather than per-screen,
    // so someone who signs in without either grant sees one clear reason why,
    // instead of eight screens each failing their own fetch.
    if (tokenVerified && !hasAnyAccess(user)) {
        return <NotGranted onSignOut={() => persistor.purge().then(() => window.location.replace(APP_ROUTES.LOGIN))} />;
    }

    return tokenVerified ? (
        <Switch>
            {roleBasedRoutes.map((route, index) => {
                return (
                    <Route
                        key={index}
                        path={route.path}
                        exact={route.exact}
                        name={route.name}
                        render={(props) => (
                            <Layout>
                                <route.component {...props} />
                            </Layout>
                        )}
                    />
                );
            })}

            <Redirect to={homeRoute(user)} />
        </Switch>
    ) : !tokenVerified && isLoading ? (
        <Loader classes="vh-100" showVerifyText={true} />
    ) : // <Page500 />
    null;
};

export default DefaultLayout;
